import React, { createContext, useContext, useEffect, useState } from 'react';
import { authService } from '../../services/authServiceBFF';
import { LoginPage } from './LoginPage';
import { PermissionsService, UserPermissions, UserInfo } from '../../services/permissionsService';

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserInfo | null;
  permissions: UserPermissions;
  loading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProviderBFF');
  }
  return context;
};

interface AuthProviderBFFProps {
  children: React.ReactNode;
}

export const AuthProviderBFF: React.FC<AuthProviderBFFProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserInfo | null>(null);
  const [permissions, setPermissions] = useState<UserPermissions>({
    inAppMessages: false,
    comments: false,
    commentSettings: false,
    taLocals: false,
    tidePoints: false,
    analytics: false,
    userManagement: false,
    contentModeration: false,
    volunteerEvents: false,
    trailObservations: false,
    isSuperAdmin: false
  });
  const [loading, setLoading] = useState(true);
  const [loggingIn, setLoggingIn] = useState(false);

  const checkAuth = async () => {
    try {
      const status = await authService.checkAuthStatus();
      setIsAuthenticated(status.isAuthenticated);
      
      if (status.isAuthenticated) {
        const userInfo = await authService.getUserInfo();
        setUser(userInfo);
        setPermissions(PermissionsService.getUserPermissions(userInfo));
      } else {
        setUser(null);
        setPermissions({
          inAppMessages: false,
          comments: false,
          commentSettings: false,
          taLocals: false,
          tidePoints: false,
          analytics: false,
          userManagement: false,
          contentModeration: false,
          volunteerEvents: false,
          trailObservations: false,
          isSuperAdmin: false
        });
      }
    } catch (error) {
      console.error('Error checking auth:', error);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async () => {
    setLoggingIn(true);
    try {
      await authService.login();
    } catch (error) {
      console.error('Login failed:', error);
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    await authService.logout();
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading The Trail App...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, show beautiful login page
  if (!isAuthenticated) {
    return <LoginPage onLogin={login} loading={loggingIn} />;
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, permissions, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};